import { bindActionCreators } from '@reduxjs/toolkit';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ChatBlkDispatch, chatFrameSlice, querySlice } from '../store/store';

/**
 * Custom hook that provides access to the ChatBlk redux store.
 *
 * This hook combines the state selectors and action dispatchers,
 * making them available in a single object.
 *
 * @returns An object containing:
 * - The current state values from the store, keyed by their selector names.
 * - The action dispatchers, keyed by their action names.
 * - The `dispatch` function for dispatching actions directly.
 */
const useChatBlkStore = () => {
    const dispatch = useDispatch<ChatBlkDispatch>();
    const state = useSelector(
        (state: {
            querySlice: ReturnType<typeof querySlice.reducer>;
            chatFrameSlice: ReturnType<typeof chatFrameSlice.reducer>;
        }) => {
            return { ...state.querySlice, ...state.chatFrameSlice };
        },
        shallowEqual
    ) as ReturnType<typeof querySlice.reducer> &
        ReturnType<typeof chatFrameSlice.reducer>;

    const dispatchActions = bindActionCreators(
        {
            ...chatFrameSlice.actions,
            ...querySlice.actions,
        },
        dispatch
    );

    return { ...state, ...dispatchActions, dispatch };
};

export default useChatBlkStore;
